import React, { useState } from 'react';
import { isUsernameTaken, finishGoogleSignup } from '../../../core/firebase/auth_service';

export default function GoogleUsernameStep({ googleUser, onSuccess, onBack }) {
  const [username, setUsername] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const validate = (val) => {
    if (val.length < 3)  return 'Minimum 3 characters';
    if (val.length > 30) return 'Maximum 30 characters';
    if (!/^[a-zA-Z0-9_.]+$/.test(val)) return 'Letters, numbers, _ and . only';
    return '';
  };

  const handleChange = (e) => {
    const val = e.target.value.toLowerCase().replace(/\s/g, '');
    setUsername(val);
    setError(validate(val));
  };

  const handleConfirm = async () => {
    const validErr = validate(username);
    if (validErr) { setError(validErr); return; }
    setLoading(true);
    setError('');
    try {
      const taken = await isUsernameTaken(username);
      if (taken) {
        setError('This username is already taken.');
        return;
      }
      await finishGoogleSignup({ user: googleUser, username });
      onSuccess();
    } catch (err) {
      console.error('Google signup error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const photoURL = googleUser?.photoURL;
  const displayName = googleUser?.displayName || googleUser?.email || '';

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button style={styles.back} onClick={onBack}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span style={styles.stepLabel}>Almost done!</span>
        <div style={{ width: 36 }} />
      </div>

      <div style={styles.content}>
        {/* Google avatar */}
        <div style={styles.avatarWrap}>
          {photoURL
            ? <img src={photoURL} alt="" style={styles.avatar} />
            : <div style={styles.avatarFallback}>{displayName[0]?.toUpperCase() || '?'}</div>
          }
          <div style={styles.googleBadge}>G</div>
        </div>

        <p style={styles.greeting}>Welcome, {displayName.split(' ')[0]}!</p>

        <h2 style={styles.title}>Choose your username</h2>
        <p style={styles.sub}>Pick a unique name for your Nexsap profile</p>

        <div style={styles.inputWrapper}>
          <span style={styles.at}>@</span>
          <input
            style={styles.input}
            placeholder="your_username"
            value={username}
            onChange={handleChange}
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
          />
        </div>

        {error ? (
          <p style={styles.error}>{error}</p>
        ) : username.length >= 3 ? (
          <p style={styles.ok}>Available</p>
        ) : null}
      </div>

      <div style={styles.bottom}>
        <button
          style={{ ...styles.btnNext, opacity: username.length < 3 || loading ? 0.5 : 1 }}
          onClick={handleConfirm}
          disabled={username.length < 3 || loading}
        >
          {loading ? 'Setting up your account...' : 'Get started'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f5f7ff', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px' },
  back: { background: 'none', border: 'none', cursor: 'pointer', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  stepLabel: { color: '#333', fontSize: 15, fontWeight: 600 },
  content: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 24px 20px' },
  avatarWrap: { position: 'relative', marginBottom: 16 },
  avatar: { width: 80, height: 80, borderRadius: 40, objectFit: 'cover', border: '3px solid #fff', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
  avatarFallback: { width: 80, height: 80, borderRadius: 40, background: '#4361ee', color: '#fff', fontSize: 32, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  googleBadge: { position: 'absolute', bottom: 0, right: 0, width: 26, height: 26, borderRadius: 13, background: '#fff', border: '2px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#4285F4', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' },
  greeting: { fontSize: 15, color: '#888', margin: '0 0 6px' },
  title: { fontSize: 26, fontWeight: 700, color: '#1a1a2e', marginBottom: 8, textAlign: 'center' },
  sub: { fontSize: 14, color: '#888', marginBottom: 32, textAlign: 'center' },
  inputWrapper: { display: 'flex', alignItems: 'center', background: '#fff', border: '1.5px solid #dde0f0', borderRadius: 14, padding: '0 16px', width: '100%', maxWidth: 380, height: 54 },
  at: { color: '#999', fontSize: 18, marginRight: 8 },
  input: { flex: 1, border: 'none', outline: 'none', fontSize: 16, color: '#222', background: 'transparent' },
  error: { color: '#e74c3c', fontSize: 13, marginTop: 10 },
  ok: { color: '#27ae60', fontSize: 13, marginTop: 10, fontWeight: 500 },
  bottom: { padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' },
  btnNext: { background: '#3d5ef5', color: '#fff', border: 'none', borderRadius: 14, height: 54, fontSize: 16, fontWeight: 600, cursor: 'pointer', width: '100%', maxWidth: 480, transition: 'opacity 0.2s' },
};
